import Close from "@mui/icons-material/Close";
import EditSquare from "@mui/icons-material/EditSquare";
import ExpandMore from "@mui/icons-material/ExpandMore";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import PrivacyTipOutlined from "@mui/icons-material/PrivacyTipOutlined";
import Route from "@mui/icons-material/Route";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Skeleton,
  Slide,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ScoutSidePanelIcon from "components/ScoutSidePanelIcon";
import { StatusChip } from "components/StatusChip";
import { TextInput } from "components/TextInput";
import { useCallback, useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "./hooks/scoutWriteMode";
import { ScoutConversationSettingsMenu } from "./ScoutConversationSettingsMenu";
import {
  SCOUT_INFO_PANEL_TABS,
  SCOUT_INFO_PANEL_WIDTH_PX,
  SCOUT_SIDE_RAIL_WIDTH_PX,
  ScoutInfoPanelContentBody,
  type ScoutInfoPanelTab,
  type ScoutSidePanelView,
} from "./ScoutInfoPanel";
import { ScoutThreadListSection, useScoutThreadsSearch, type ScoutThreadListItem } from "./Search";

const PANEL_HEADER_HEIGHT_SPACING = 7;
/** Side-panel scroll track width; header overlay stops here so the track shows. */
const PANEL_SCROLLBAR_WIDTH_PX = 12;

// Shared icons for the collapsed rail and the expanded-panel footer chips so a
// new/renamed tab in SCOUT_INFO_PANEL_TABS won't compile until this is updated.
const SCOUT_INFO_TAB_ICON: Record<ScoutInfoPanelTab, ReactElement> = {
  sources: <LibraryBooks fontSize="small" />,
  method: <Route fontSize="small" />,
  privacy: <PrivacyTipOutlined fontSize="small" />,
};

const SCOUT_INFO_TAB_CHIP_ICON: Record<ScoutInfoPanelTab, ReactElement> = {
  sources: <LibraryBooks sx={{ fontSize: 16 }} />,
  method: <Route sx={{ fontSize: 16 }} />,
  privacy: <PrivacyTipOutlined sx={{ fontSize: 16 }} />,
};

type ScoutSidePanelProps = {
  open: boolean;
  view: ScoutSidePanelView;
  tab: ScoutInfoPanelTab | null;
  isPersistentMode: boolean;
  onTogglePanel: () => void;
  onOpenView: (view: ScoutSidePanelView) => void;
  onOpenSearch: () => void;
  onClose: () => void;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onClearTab: () => void;
};

export const RECENTS_PAGE_SIZE = 12;
const SCROLL_BOUNDARY_TOLERANCE_PX = 1;

function allowsVerticalScroll(element: HTMLElement) {
  const overflowY = window.getComputedStyle(element).overflowY;
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
}

function getScrollableAncestorWithinPanel(target: EventTarget | null, boundary: HTMLElement) {
  if (!(target instanceof Element)) {
    return null;
  }

  let element: Element | null = target;
  while (element && boundary.contains(element)) {
    if (
      element instanceof HTMLElement &&
      allowsVerticalScroll(element) &&
      element.scrollHeight > element.clientHeight
    ) {
      return element;
    }

    if (element === boundary) {
      return null;
    }

    element = element.parentElement;
  }

  return null;
}

function canScrollWithDelta(element: HTMLElement, deltaY: number) {
  if (deltaY < 0) {
    return element.scrollTop > 0;
  }

  if (deltaY > 0) {
    return (
      element.scrollTop + element.clientHeight < element.scrollHeight - SCROLL_BOUNDARY_TOLERANCE_PX
    );
  }

  return true;
}

type ScoutConversationHeaderProps = {
  conversationTitle?: string;
  showTitle: boolean;
  writeMode?: ScoutWriteModeGql;
  onWriteModeChange?: (next: ScoutWriteModeGql) => void;
  settingsDisabled?: boolean;
  hasScoutProAccess?: boolean;
  /** Extra content rendered at the right end, before the settings menu. */
  endContent?: ReactNode;
};

export function ScoutConversationHeader({
  conversationTitle,
  showTitle,
  writeMode = SCOUT_WRITE_MODE_GQL.OFF,
  onWriteModeChange,
  settingsDisabled = false,
  hasScoutProAccess = true,
  endContent,
}: ScoutConversationHeaderProps) {
  const title = conversationTitle?.trim();

  return (
    <Stack
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 1,
        pointerEvents: "auto",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{
          minWidth: 0,
          flex: 1,
          pr: 2,
          pl: 1,
        }}
      >
        {showTitle && title && (
          <Typography
            variant="h6"
            color="text.secondary"
            noWrap
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              fontWeight: 400,
              lineHeight: 1.4,
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
        )}
        {showTitle && !title && (
          <Skeleton
            variant="text"
            width={220}
            sx={{
              fontSize: { xs: "1rem", sm: "1.125rem" },
              lineHeight: 1.4,
            }}
          />
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
        {endContent}
        <ScoutConversationSettingsMenu
          writeMode={writeMode}
          onWriteModeChange={onWriteModeChange ?? (() => undefined)}
          disabled={settingsDisabled}
          hasScoutProAccess={hasScoutProAccess}
        />
      </Stack>
    </Stack>
  );
}

export function ScoutSidePanel({
  open,
  view,
  tab,
  isPersistentMode,
  onTogglePanel,
  onOpenView,
  onOpenSearch,
  onClose,
  onTabChange,
  onClearTab,
}: ScoutSidePanelProps) {
  const theme = useTheme();
  const showRail = !open;
  const drawerBody = (
    <ScoutSidePanelDrawerContent
      open={open}
      view={view}
      tab={tab}
      isPersistentMode={isPersistentMode}
      onOpenSearch={onOpenSearch}
      onClose={onClose}
      onTabChange={onTabChange}
      onClearTab={onClearTab}
    />
  );

  // Mobile: temporary overlay drawer plus a rail when collapsed.
  if (!isPersistentMode) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flexShrink: 0,
          height: "100%",
        }}
      >
        <Drawer
          anchor="right"
          variant="temporary"
          open={open}
          onClose={onClose}
          disableScrollLock
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            },
          }}
        >
          {drawerBody}
        </Drawer>

        {showRail ? (
          <ScoutSidePanelRail
            open={open}
            tab={tab}
            onTogglePanel={onTogglePanel}
            onOpenView={onOpenView}
            onOpenSearch={onOpenSearch}
          />
        ) : null}
      </Box>
    );
  }

  // Desktop: a single container whose width animates between the collapsed rail
  // and the expanded panel, crossfading their contents — mirrors LeftNavV2.
  const widthTransition = theme.transitions.create("width", {
    easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  });
  const opacityTransition = theme.transitions.create("opacity", {
    duration: theme.transitions.duration.shortest,
  });

  return (
    <Box
      sx={{
        position: "relative",
        flexShrink: 0,
        height: "100%",
        overflow: "hidden",
        width: open ? SCOUT_INFO_PANEL_WIDTH_PX : SCOUT_SIDE_RAIL_WIDTH_PX,
        transition: widthTransition,
        borderRight: 0.5,
        borderColor: "divider",
      }}
    >
      {/* The rail lives in normal flow so it always paints; the expanded panel
          fades in on top of it, mirroring LeftNavV2's collapse behavior.
          When collapsed, keep the rail above the invisible overlay so the
          overlay's floating header (which re-enables pointer-events on its
          children) cannot steal hover/clicks from the rail buttons. */}
      <Box
        sx={{
          position: "relative",
          zIndex: open ? 0 : 1,
          height: "100%",
        }}
      >
        <ScoutSidePanelRail
          open={open}
          tab={tab}
          onTogglePanel={onTogglePanel}
          onOpenView={onOpenView}
          onOpenSearch={onOpenSearch}
        />
      </Box>

      <Box
        aria-hidden={!open}
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: open ? 1 : 0,
          width: SCOUT_INFO_PANEL_WIDTH_PX,
          bgcolor: "background.paper",
          borderLeft: 1,
          borderColor: "divider",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: opacityTransition,
        }}
      >
        {drawerBody}
      </Box>
    </Box>
  );
}

function ScoutSidePanelRail({
  open,
  tab,
  onTogglePanel,
  onOpenView,
  onOpenSearch,
}: {
  open: boolean;
  tab: ScoutInfoPanelTab | null;
  onTogglePanel: () => void;
  onOpenView: (view: ScoutSidePanelView) => void;
  onOpenSearch: () => void;
}) {
  const navigate = useNavigate();
  const { tenantId } = useParams();

  const handleNewChat = useCallback(() => {
    navigate(`/o/${tenantId}/scout`);
  }, [navigate, tenantId]);

  const topRailItems: {
    key: "search" | "new-chat";
    label: string;
    icon: ReactNode;
    onClick: () => void;
  }[] = [
    {
      key: "search",
      label: "Search chats",
      icon: <SearchIcon fontSize="small" />,
      onClick: onOpenSearch,
    },
    {
      key: "new-chat",
      label: "New chat",
      icon: <EditSquare fontSize="small" />,
      onClick: handleNewChat,
    },
  ];

  const infoRailItems = SCOUT_INFO_PANEL_TABS.map(({ value, label }) => ({
    key: value,
    label,
    icon: SCOUT_INFO_TAB_ICON[value],
    onClick: () => onOpenView(value),
    selected: open && tab === value,
  }));

  return (
    <Stack
      component="nav"
      aria-label="Scout actions"
      sx={{
        width: SCOUT_SIDE_RAIL_WIDTH_PX,
        height: "100%",
        flexShrink: 0,
        alignItems: "center",
        py: 1.5,
        gap: 1.5,
        bgcolor: "background.paper",
        borderLeft: 1,
        borderColor: "divider",
      }}
    >
      <Tooltip title="Open panel" placement="left">
        <IconButton size="small" onClick={onTogglePanel} aria-label="Open panel">
          <ScoutSidePanelIcon fontSize="small" open={open} />
        </IconButton>
      </Tooltip>

      <Divider flexItem sx={{ mx: 1 }} />

      {topRailItems.map((item) => (
        <Tooltip key={item.key} title={item.label} placement="left">
          <IconButton size="small" onClick={item.onClick} aria-label={item.label}>
            {item.icon}
          </IconButton>
        </Tooltip>
      ))}

      <Divider flexItem sx={{ mx: 1 }} />

      {infoRailItems.map((item) => (
        <Tooltip key={item.key} title={item.label} placement="left">
          <IconButton
            size="small"
            onClick={item.onClick}
            aria-label={item.label}
            aria-pressed={item.selected}
            color={item.selected ? "primary" : "default"}
            sx={{
              ...(item.selected && {
                bgcolor: "action.selected",
              }),
            }}
          >
            {item.icon}
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
}

function ScoutSidePanelDrawerContent({
  open,
  view,
  tab,
  isPersistentMode,
  onOpenSearch,
  onClose,
  onTabChange,
  onClearTab,
}: {
  open: boolean;
  view: ScoutSidePanelView;
  tab: ScoutInfoPanelTab | null;
  isPersistentMode: boolean;
  onOpenSearch: () => void;
  onClose: () => void;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
  onClearTab: () => void;
}) {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const [searchInput, setSearchInput] = useState("");
  const searchInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const isSearchView = view === "search";
  const isInfoSheetOpen = tab !== null;
  const hasSearchTerm = searchInput.trim().length > 0;

  const handleSelectThread = useCallback(
    (thread: ScoutThreadListItem) => {
      navigate(`/o/${tenantId}/scout/${thread.id}`);
      if (!isPersistentMode) {
        onClose();
      }
    },
    [isPersistentMode, navigate, onClose, tenantId]
  );

  const handleNewChat = useCallback(() => {
    navigate(`/o/${tenantId}/scout`);
    if (!isPersistentMode) {
      onClose();
    }
  }, [isPersistentMode, navigate, onClose, tenantId]);

  // Clear the current search term without leaving the search view.
  const handleClearSearch = useCallback(() => {
    setSearchInput("");
  }, []);

  // Contain wheel scroll inside the panel's own scroll surfaces (list + info
  // sheet). Must be a native non-passive listener — React's onWheel is
  // passive, so preventDefault would warn and fail.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const handleWheel = (event: globalThis.WheelEvent) => {
      if (event.defaultPrevented || event.deltaY === 0) {
        return;
      }

      const scrollableAncestor = getScrollableAncestorWithinPanel(event.target, panel);
      if (!scrollableAncestor || !canScrollWithDelta(scrollableAncestor, event.deltaY)) {
        event.preventDefault();
      }
    };

    panel.addEventListener("wheel", handleWheel, { passive: false, capture: true });
    return () => {
      panel.removeEventListener("wheel", handleWheel, { capture: true });
    };
  }, []);

  const focusSearchInput = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition);
  }, []);

  const handleSearchFieldActivate = useCallback(() => {
    // Focusing / opening search dismisses the info sheet so the list surface
    // isn't competing with the slide-up content.
    onClearTab();
    if (!isSearchView) {
      onOpenSearch();
    }
  }, [isSearchView, onClearTab, onOpenSearch]);

  // Focus the field whenever the embedded search view appears.
  useEffect(() => {
    if (!open || !isSearchView) {
      return;
    }

    const animationFrameId = window.requestAnimationFrame(focusSearchInput);
    return () => window.cancelAnimationFrame(animationFrameId);
  }, [focusSearchInput, isSearchView, open]);

  // Clear the query when the panel closes so it reopens with an empty search.
  // Adjust during render (per the React docs) rather than in an effect.
  const wasOpenRef = useRef(open);
  if (wasOpenRef.current !== open) {
    wasOpenRef.current = open;
    if (!open && searchInput !== "") {
      setSearchInput("");
    }
  }

  return (
    <Box
      ref={panelRef}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        width: "100%",
      }}
    >
      {/* Scroll surface spans from the panel top to the footer so the track
          runs beside the search/close header (same overlay pattern as the
          full-page Scout chat column). */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "auto",
            overflowX: "hidden",
            // Reserve room for the floating header + divider.
            pt: (theme) => `calc(${theme.spacing(PANEL_HEADER_HEIGHT_SPACING)} + 1px)`,
            scrollbarWidth: "thin",
            scrollbarColor: (theme) =>
              `${theme.palette.scrollbarThumb} ${theme.palette.background.paper}`,
            "&::-webkit-scrollbar": {
              width: `${PANEL_SCROLLBAR_WIDTH_PX}px`,
              backgroundColor: (theme) => theme.palette.background.paper,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: (theme) => theme.palette.background.paper,
              border: "none",
              boxShadow: "none",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.scrollbarThumb,
              border: "none",
              borderRadius: 999,
            },
            "&::-webkit-scrollbar-corner": {
              backgroundColor: (theme) => theme.palette.background.paper,
            },
          }}
        >
          {isSearchView ? (
            <Box sx={{ pl: 1.5, pb: 2 }}>
              <ScoutSidePanelSearchContent
                open={open}
                searchInput={searchInput}
                onSearchInputChange={setSearchInput}
                onSelectThread={handleSelectThread}
              />
            </Box>
          ) : (
            <Box sx={{ pb: 2 }}>
              <ScoutSidePanelRecentsContent open={open} onSelectThread={handleSelectThread} />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            // Stop short of the scrollbar so the track stays visible beside
            // the close control.
            right: `${PANEL_SCROLLBAR_WIDTH_PX}px`,
            zIndex: 1,
            bgcolor: "background.paper",
            pointerEvents: "none",
            "& > *": { pointerEvents: "auto" },
          }}
        >
          <ScoutSidePanelHeader
            searchInput={searchInput}
            isSearchView={isSearchView}
            hasSearchTerm={hasSearchTerm}
            inputRef={searchInputRef}
            onSearchInputChange={setSearchInput}
            onActivateSearch={handleSearchFieldActivate}
            onNewChat={handleNewChat}
            onClearSearch={handleClearSearch}
            onClose={onClose}
          />
          <Divider sx={{ mx: 2 }} />
        </Box>

        {/* Info sheet covers the list surface up to the search header. */}
        <Slide direction="up" in={isInfoSheetOpen} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "absolute",
              // Sit just below the floating search header + divider.
              top: (theme) => `calc(${theme.spacing(PANEL_HEADER_HEIGHT_SPACING)} + 1px)`,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2,
              bgcolor: "background.paper",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
              <Tooltip title="Hide information panel">
                <IconButton
                  size="small"
                  onClick={onClearTab}
                  aria-label="Hide information panel"
                  sx={{
                    position: "absolute",
                    // Match the close-panel button: header uses px: 1.5 and
                    // stops at the scrollbar gutter, so this lands on the same
                    // vertical line.
                    top: (theme) => theme.spacing(2),
                    right: (theme) => `calc(${PANEL_SCROLLBAR_WIDTH_PX}px + ${theme.spacing(1.5)})`,
                    mt: -1,
                    zIndex: 1,
                    bgcolor: "background.paper",
                    "&:hover": { bgcolor: "background.paper" },
                  }}
                >
                  <ExpandMore fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  height: "100%",
                  overflow: "auto",
                  overflowX: "hidden",
                  // Keep the title clear of the absolute chevron.
                  pr: 3,
                  scrollbarWidth: "thin",
                  scrollbarColor: (theme) =>
                    `${theme.palette.scrollbarThumb} ${theme.palette.background.paper}`,
                  "&::-webkit-scrollbar": {
                    width: `${PANEL_SCROLLBAR_WIDTH_PX}px`,
                    backgroundColor: (theme) => theme.palette.background.paper,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                    border: "none",
                    boxShadow: "none",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: (theme) => theme.palette.scrollbarThumb,
                    border: "none",
                    borderRadius: 999,
                  },
                  "&::-webkit-scrollbar-corner": {
                    backgroundColor: (theme) => theme.palette.background.paper,
                  },
                }}
              >
                {tab ? <ScoutInfoPanelContentBody tab={tab} scrollable={false} /> : null}
              </Box>
            </Box>
          </Box>
        </Slide>
      </Box>

      <ScoutSidePanelFooter tab={tab} onTabChange={onTabChange} />
    </Box>
  );
}

function ScoutSidePanelHeader({
  searchInput,
  isSearchView,
  hasSearchTerm,
  inputRef,
  onSearchInputChange,
  onActivateSearch,
  onNewChat,
  onClearSearch,
  onClose,
}: {
  searchInput: string;
  isSearchView: boolean;
  hasSearchTerm: boolean;
  inputRef: React.Ref<HTMLInputElement | HTMLTextAreaElement | null>;
  onSearchInputChange: (value: string) => void;
  onActivateSearch: () => void;
  onNewChat: () => void;
  onClearSearch: () => void;
  onClose: () => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minHeight: (theme) => theme.spacing(PANEL_HEADER_HEIGHT_SPACING),
        px: 1.5,
        flexShrink: 0,
      }}
    >
      <TextInput
        placeholder="Search chats"
        fullWidth
        inputRef={inputRef}
        // Keep the query visible when browsing info tabs so leaving search
        // doesn't look like the term was discarded (clicking the field
        // returns to the same results).
        value={searchInput}
        readOnly={!isSearchView}
        onChange={isSearchView ? (e) => onSearchInputChange(e.target.value) : undefined}
        // Clicking the field dismisses the info sheet and opens search when
        // needed. (Using `onClick` rather than `onFocus` avoids re-opening the
        // desktop dialog when focus is restored to the field on dialog close.)
        onClick={onActivateSearch}
        onKeyDown={
          isSearchView
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onActivateSearch();
                }
              }
        }
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        }
        endAdornment={
          hasSearchTerm ? (
            <InputAdornment position="end">
              <Tooltip title="Clear search">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClearSearch();
                  }}
                  aria-label="Clear search"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ) : undefined
        }
      />
      <Tooltip title="New chat">
        <IconButton size="small" onClick={onNewChat} aria-label="New chat">
          <EditSquare fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Close panel">
        <IconButton size="small" onClick={onClose} aria-label="Close panel">
          <ScoutSidePanelIcon fontSize="small" open />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function ScoutSidePanelFooter({
  tab,
  onTabChange,
}: {
  tab: ScoutInfoPanelTab | null;
  onTabChange: (tab: ScoutInfoPanelTab) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.5,
        py: 1,
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        role="tablist"
        aria-label="Scout information panel"
        sx={{ gap: 1, flexWrap: "nowrap" }}
      >
        {SCOUT_INFO_PANEL_TABS.map(({ value, label }) => {
          const selected = tab === value;

          return (
            <StatusChip
              key={value}
              icon={SCOUT_INFO_TAB_CHIP_ICON[value]}
              label={label}
              color={selected ? "info" : "default"}
              variant="outlined"
              size="small"
              onClick={() => onTabChange(value)}
              role="tab"
              aria-selected={selected}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function ScoutSidePanelRecentsContent({
  open,
  onSelectThread,
}: {
  open: boolean;
  onSelectThread: (thread: ScoutThreadListItem) => void;
}) {
  const recents = useScoutThreadsSearch({
    enabled: open,
    pageSize: RECENTS_PAGE_SIZE,
    fetchPolicy: "cache-and-network",
  });

  return (
    <Box sx={{ pl: 1.5, pt: 2 }}>
      <ScoutThreadListSection
        heading="Recents"
        items={recents.items}
        loading={recents.isInitialLoading}
        hasMore={recents.hasMore}
        loadingMore={recents.isLoadingMore}
        emptyMessage="No recent threads."
        errorMessage={recents.errorMessage}
        onLoadMore={recents.loadMore}
        onSelectItem={onSelectThread}
        pageSize={RECENTS_PAGE_SIZE}
        compact
        scrollable={false}
      />
    </Box>
  );
}

function ScoutSidePanelSearchContent({
  open,
  searchInput,
  onSearchInputChange,
  onSelectThread,
}: {
  open: boolean;
  searchInput: string;
  onSearchInputChange: (value: string) => void;
  onSelectThread: (thread: ScoutThreadListItem) => void;
}) {
  const isSearching = searchInput.trim().length > 0;

  // Live search results reflect the latest server ordering (network-only).
  const results = useScoutThreadsSearch({
    enabled: open && isSearching,
    searchInput,
    onSearchInputChange,
    fetchPolicy: "network-only",
  });

  // Recents paint instantly from cache while the empty search view is shown.
  const recents = useScoutThreadsSearch({
    enabled: open && !isSearching,
    pageSize: RECENTS_PAGE_SIZE,
    fetchPolicy: "cache-and-network",
  });

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <ScoutThreadListSection
        heading="Search results"
        items={isSearching ? results.items : []}
        loading={isSearching && results.isInitialLoading}
        hasMore={isSearching && results.hasMore}
        loadingMore={isSearching && results.isLoadingMore}
        emptyMessage={
          isSearching
            ? `We couldn't find any results for "${searchInput.trim()}"\n\nTry again using keywords`
            : "Search your chats to see results."
        }
        errorMessage={isSearching ? results.errorMessage : undefined}
        onLoadMore={results.loadMore}
        onSelectItem={onSelectThread}
        compact
        scrollable={false}
      />

      {!isSearching ? (
        <ScoutThreadListSection
          heading="Recents"
          items={recents.items}
          loading={recents.isInitialLoading}
          hasMore={recents.hasMore}
          loadingMore={recents.isLoadingMore}
          emptyMessage="No recent threads."
          errorMessage={recents.errorMessage}
          onLoadMore={recents.loadMore}
          onSelectItem={onSelectThread}
          pageSize={RECENTS_PAGE_SIZE}
          compact
          scrollable={false}
        />
      ) : null}
    </Stack>
  );
}
